import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import heroEditorial from "@/assets/hero-editorial.jpg";
import editorialBoardroom from "@/assets/editorial-boardroom.jpg";
import editorialLobby from "@/assets/editorial-lobby.jpg";
import editorialTerrace from "@/assets/editorial-terrace.jpg";
import editorialCourtyard from "@/assets/editorial-courtyard.jpg";
import editorialEntrance from "@/assets/editorial-entrance.jpg";
import editorialDining from "@/assets/editorial-dining.jpg";
import editorialStaircase from "@/assets/editorial-staircase.jpg";

type RouteMeta = {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
};

const SITE_DOMAIN = "https://venuevine.co.uk";

const routeMeta: Record<string, RouteMeta> = {
  "/": {
    title: "Corporate Venue Sourcing London | Venue Vine",
    description:
      "Venue Vine is a specialist corporate venue sourcing and intelligence partner for commercially driven teams across London and the UK. Expert briefing, independent recommendations, and intelligent negotiation — tell us about your event.",
    keywords:
      "corporate venue sourcing London, venue finding service London, corporate event venues London, leadership retreat venues UK, conference venue sourcing London, venue consultancy London",
    image: heroEditorial,
  },
  "/services": {
    title: "Corporate Venue Sourcing Services | Venue Vine London",
    description:
      "Structured venue sourcing for corporate teams. We source, shortlist, and negotiate venues for meetings, conferences and corporate events.",
    keywords:
      "venue sourcing services, corporate venue consultancy, venue negotiation London, event venue shortlist",
    image: editorialBoardroom,
  },
  "/faqs": {
    title: "Venue Sourcing FAQs | Venue Vine London",
    description:
      "Frequently asked questions about Venue Vine's venue sourcing services, process and pricing.",
    keywords:
      "venue sourcing FAQ, corporate venue questions, venue finder process",
    image: editorialStaircase,
  },
  "/blog": {
    title: "The Commercial Edge Series | Venue Vine Articles",
    description:
      "Articles on venue negotiation, commercial terms and corporate event decision-making, written for teams booking venues in London and the UK.",
    keywords:
      "venue insights London, corporate event articles, venue negotiation guidance, event planning guidance",
    image: editorialCourtyard,
  },
  "/insight": {
    title: "Commercial Insight Summary | Venue Vine London",
    description:
      "Review Venue Vine's commercial insight approach for evaluating venue value, negotiation opportunities, and event decision-making.",
    keywords:
      "venue commercial insight, venue negotiation strategy, event decision-making",
    image: editorialDining,
  },
  "/intelligence": {
    title: "Venue Intelligence Report | Venue Vine London",
    description:
      "A structured read on the London corporate venue market: availability, rate movement and where commercial value currently sits.",
    keywords:
      "venue intelligence report, London corporate venues, venue market analysis",
    image: editorialTerrace,
  },
  "/venues": {
    title: "Venues & Partnerships | Venue Vine London",
    description:
      "Commercial collaboration for venues seeking qualified corporate demand. Partnership tiers, criteria and how the model works.",
    keywords:
      "venue partnership London, corporate venue partner, venue commercial collaboration",
    image: editorialLobby,
  },
  "/contact": {
    title: "Contact Venue Vine | Share Your Venue Brief",
    description:
      "Share a corporate venue brief or enquire about venue partnership. We respond within one working day.",
    keywords:
      "contact venue sourcing London, venue brief, corporate event enquiry",
    image: editorialEntrance,
  },
  "/partner": {
    title: "Partner Login | Venue Vine",
    description:
      "Secure sign-in for Venue Vine venue partners to manage their venue profile and partnership details.",
  },
  "/partner/onboarding": {
    title: "Partner Onboarding | Venue Vine",
    description:
      "Onboard your venue with Venue Vine to be considered for curated corporate event opportunities in London.",
  },
};

const ensureMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
  }

  return element;
};

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const meta = routeMeta[location.pathname] ?? routeMeta["/"];
    const canonicalUrl = location.pathname === "/" ? `${SITE_DOMAIN}/` : `${SITE_DOMAIN}${location.pathname}`;

    document.title = meta.title;

    const descriptionTag = ensureMeta('meta[name="description"]', { name: "description" });
    descriptionTag.setAttribute("content", meta.description);

    const robotsTag = ensureMeta('meta[name="robots"]', { name: "robots" });
    robotsTag.setAttribute("content", "index, follow, max-image-preview:large");

    if (meta.keywords) {
      const keywordsTag = ensureMeta('meta[name="keywords"]', { name: "keywords" });
      keywordsTag.setAttribute("content", meta.keywords);
    }

    const ogTitle = ensureMeta('meta[property="og:title"]', { property: "og:title" });
    ogTitle.setAttribute("content", meta.title);

    const ogDescription = ensureMeta('meta[property="og:description"]', { property: "og:description" });
    ogDescription.setAttribute("content", meta.description);

    const ogUrl = ensureMeta('meta[property="og:url"]', { property: "og:url" });
    ogUrl.setAttribute("content", canonicalUrl);

    const twitterTitle = ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" });
    twitterTitle.setAttribute("content", meta.title);

    const twitterDescription = ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" });
    twitterDescription.setAttribute("content", meta.description);

    if (meta.image) {
      const absoluteImage = meta.image.startsWith("http")
        ? meta.image
        : `${SITE_DOMAIN}${meta.image}`;

      const ogImage = ensureMeta('meta[property="og:image"]', { property: "og:image" });
      ogImage.setAttribute("content", absoluteImage);

      const twitterImage = ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" });
      twitterImage.setAttribute("content", absoluteImage);
    }

    let canonicalLink = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalUrl);

    let structuredDataScript = document.getElementById("seo-jsonld") as HTMLScriptElement | null;
    if (!structuredDataScript) {
      structuredDataScript = document.createElement("script");
      structuredDataScript.id = "seo-jsonld";
      structuredDataScript.type = "application/ld+json";
      document.head.appendChild(structuredDataScript);
    }

    structuredDataScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: "Venue Vine",
          url: SITE_DOMAIN,
          description: "Corporate venue sourcing and event consultancy in London, United Kingdom.",
          email: "hello@venuevine.co.uk",
          address: {
            "@type": "PostalAddress",
            addressLocality: "London",
            addressCountry: "GB",
          },
          sameAs: [
            "https://www.linkedin.com/company/venue-vine",
          ],
        },
        {
          "@type": "WebSite",
          name: "Venue Vine",
          url: SITE_DOMAIN,
        },
        {
          "@type": "WebPage",
          name: meta.title,
          description: meta.description,
          url: canonicalUrl,
          isPartOf: {
            "@type": "WebSite",
            name: "Venue Vine",
            url: SITE_DOMAIN,
          },
        },
        {
          "@type": "LocalBusiness",
          name: "Venue Vine",
          description: "Corporate venue sourcing and event consultancy.",
          url: SITE_DOMAIN,
          email: "hello@venuevine.co.uk",
          address: {
            "@type": "PostalAddress",
            addressLocality: "London",
            addressCountry: "GB",
          },
          priceRange: "££",
          areaServed: {
            "@type": "Place",
            name: "London, United Kingdom",
          },
        },
      ],
    });
  }, [location.pathname]);

  return null;
};

export default SeoManager;
